import { useEffect, useRef } from 'react';

const FOCUSABLE = 'a[href], button, input:not([type="hidden"]), select, textarea, [tabindex], [contenteditable="true"]';
const ARROW_CONTROL = 'input, textarea, select, [contenteditable="true"], [role="slider"], [role="spinbutton"], [role="combobox"]';

const getFocusable = (dialog) => Array.from(dialog.querySelectorAll(FOCUSABLE)).filter((element) =>
  element.tabIndex >= 0 && !element.matches(':disabled') &&
  !element.closest('[hidden], [inert]') && element.getClientRects().length > 0 &&
  window.getComputedStyle(element).visibility !== 'hidden'
);

// Keep one focus/scroll session per opening. Callback changes must not restore
// the opener or move focus back to Close while someone is using the dialog.
export default function useModalDialog({ open, dialogRef, initialFocusRef, onClose, onPrevious, onNext }) {
  const callbacks = useRef({ onClose, onPrevious, onNext });
  useEffect(() => {
    callbacks.current = { onClose, onPrevious, onNext };
  }, [onClose, onPrevious, onNext]);

  useEffect(() => {
    if (!open || !dialogRef.current) return;
    const dialog = dialogRef.current;
    const opener = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusFirst = () => (getFocusable(dialog)[0] || dialog).focus({ preventScroll: true });
    const keepFocusInside = (event) => {
      if (!dialog.contains(event.target)) focusFirst();
    };
    const onKey = (event) => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        callbacks.current.onClose?.();
        return;
      }
      if (event.key === 'Tab') {
        const items = getFocusable(dialog);
        const first = items[0];
        const last = items[items.length - 1];
        if (!first) {
          event.preventDefault();
          dialog.focus({ preventScroll: true });
        } else if (!dialog.contains(document.activeElement) || document.activeElement === dialog) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus({ preventScroll: true });
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus({ preventScroll: true });
        }
        return;
      }
      // A before/after slider (or any editable control) owns its arrow keys.
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey ||
          event.target.closest?.(ARROW_CONTROL)) return;
      const navigate = event.key === 'ArrowLeft' ? callbacks.current.onPrevious
        : event.key === 'ArrowRight' ? callbacks.current.onNext : null;
      if (navigate) {
        event.preventDefault();
        navigate();
      }
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', keepFocusInside);
    (initialFocusRef.current || getFocusable(dialog)[0] || dialog).focus({ preventScroll: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', keepFocusInside);
      document.body.style.overflow = previousOverflow;
      if (opener?.isConnected && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
    };
  }, [open, dialogRef, initialFocusRef]);
}
